
import React from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Plus, RefreshCw } from 'lucide-react';
import { supabase, setEmployeePin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { getUserPinOrThrow, shouldUsePinMode } from '@/utils/pinModeUtils';

interface CreateEmployeeFormData {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
  position?: string;
  hourly_rate?: number;
  hire_date?: string;
  pin: string;
}

interface CreateEmployeeDialogProps {
  onEmployeeCreated: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CreateEmployeeDialog = ({ onEmployeeCreated, open, onOpenChange }: CreateEmployeeDialogProps) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const { toast } = useToast();
  const isOpen = typeof open === 'boolean' ? open : internalOpen;
  const handleOpenChange = (nextOpen: boolean) => {
    if (onOpenChange) onOpenChange(nextOpen);
    if (open === undefined) setInternalOpen(nextOpen);
  };
  
  const form = useForm<CreateEmployeeFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      position: '',
      hourly_rate: undefined,
      hire_date: '',
      pin: '',
    },
  });

  const onSubmit = async (data: CreateEmployeeFormData) => {
    try {
      setLoading(true);
      const generatedPassword = `PIN_${Math.random().toString(36).slice(2)}_${Date.now()}`;

      if (shouldUsePinMode()) {
        const pin = getUserPinOrThrow();

        // Sécuriser le payload RPC: aucun `undefined`, et champs requis non vides.
        // PostgREST renvoie un 404 (PGRST202) si la signature de fonction ne matche pas,
        // ce qui arrive facilement si un champ requis est omis (ex: valeur `undefined`).
        const email = data.email?.trim();
        const firstName = data.first_name?.trim();
        const lastName = data.last_name?.trim();

        if (!email) throw new Error("L'email est requis");
        if (!firstName) throw new Error('Le prénom est requis');
        if (!lastName) throw new Error('Le nom est requis');

        const rpcArgs = {
          p_pin: pin,
          p_email: email,
          p_password: generatedPassword,
          p_first_name: firstName,
          p_last_name: lastName,
          p_phone: data.phone?.trim() ? data.phone.trim() : null,
          p_position: data.position?.trim() ? data.position.trim() : null,
          p_hourly_rate: data.hourly_rate ?? null,
          p_hire_date: data.hire_date?.trim() ? data.hire_date.trim() : null,
          p_app_role: 'employe',
        };

        if (import.meta.env.DEV) {
          // Ne pas logguer le PIN ni le mot de passe; juste la forme du payload
          console.log('🧾 pin_create_employee payload:', {
            keys: Object.keys(rpcArgs),
            has_pin: Boolean(rpcArgs.p_pin),
            email: rpcArgs.p_email,
          });
        }

        const { data: newUserId, error } = await supabase.rpc('pin_create_employee', rpcArgs);
        if (error) throw error;
        if (!newUserId) throw new Error("Impossible de créer l'employé");

        const { error: pinError } = await supabase.rpc('pin_set_employee_pin', {
          p_pin: pin,
          p_profile_id: newUserId,
          p_new_pin: data.pin,
        });
        if (pinError) throw pinError;
      } else {
        // Fallback JWT (compat) - nécessite droits admin côté serveur
        const { data: authData, error: authError } = await supabase.auth.admin.createUser({
          email: data.email,
          password: generatedPassword,
          email_confirm: true,
          user_metadata: {
            first_name: data.first_name,
            last_name: data.last_name,
          },
        });

        if (authError) throw authError;
        if (!authData.user) throw new Error("Impossible de créer l'utilisateur");

        // Puis mettre à jour le profil avec des informations additionnelles
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            phone: data.phone || null,
            position: data.position || null,
            hourly_rate: data.hourly_rate || null,
            hire_date: data.hire_date || null,
            is_active: true,
          })
          .eq('id', authData.user.id);

        if (profileError) throw profileError;

        const { error: pinError } = await setEmployeePin(authData.user.id, data.pin);
        if (pinError) throw pinError;
      }

      toast({
        title: "Succès",
        description: "Employé créé avec succès",
      });

      form.reset();
      handleOpenChange(false);
      onEmployeeCreated();
    } catch (error) {
      const err = error as { message?: string; code?: string; details?: string; hint?: string };
      console.error('Erreur lors de la création:', err);

      const message =
        err?.code === 'PGRST202'
          ? "Requête invalide (RPC introuvable / paramètres manquants). Vérifiez le PIN manager et les champs requis."
          : err?.message || "Impossible de créer l'employé";
      toast({
        title: "Erreur",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePin = () => {
    const generatedPin = Math.floor(100000 + Math.random() * 900000).toString();
    form.setValue('pin', generatedPin, { shouldValidate: true, shouldDirty: true });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="h-4 w-4 mr-2" />
          Nouvel employé
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Créer un nouvel employé</DialogTitle>
          <DialogDescription>
            Remplissez le formulaire pour créer un nouvel employé. Un compte de connexion sera créé automatiquement.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                rules={{ required: "Le prénom est requis" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prénom *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="last_name"
                rules={{ required: "Le nom est requis" }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nom *</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="email"
              rules={{ 
                required: "L'email est requis",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Email invalide"
                }
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email *</FormLabel>
                  <FormControl>
                    <Input type="email" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pin"
              rules={{ 
                required: "Le PIN est requis",
                pattern: {
                  value: /^\d{6}$/,
                  message: "Le PIN doit contenir exactement 6 chiffres"
                },
              }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PIN (6 chiffres) *</FormLabel>
                  <FormControl>
                    <Input
                      inputMode="numeric"
                      maxLength={6}
                      {...field}
                      onChange={(event) =>
                        field.onChange(event.target.value.replace(/\D/g, ''))
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleGeneratePin}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Générer un PIN
            </Button>
            
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Téléphone</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="position"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Poste</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="hourly_rate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taux horaire ($/h)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hire_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date d'embauche</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Annuler
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? "Création..." : "Créer"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateEmployeeDialog;
